import type { AlertVariant } from "@replohq/lattice/blocks/alert/Alert";
import type { StatusPillAction } from "@replohq/lattice/components/status-pill/StatusPill";

import React from "react";

import { AlertIcon } from "@replohq/lattice/blocks/alert/Alert";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  StatusPill,
  StatusPillDone,
} from "@replohq/lattice/components/status-pill/StatusPill";
import { Tooltip } from "@replohq/lattice/components/tooltip/Tooltip";
import { cn } from "@replohq/lattice/utils/cn";
import { Bug, ChevronDown, X } from "lucide-react";
import { isNotNullish } from "replo-utils/lib/misc";
import { toast as sonnerToast } from "sonner";

type ToastVariant = Extract<
  AlertVariant,
  "success" | "error" | "warning" | "info"
>;

interface ToastAction {
  label: string;
  onClick: () => void;
  dismissOnClick?: boolean;
}

// A type alias so ErrorToastShownEvent, built on it, still fits Record<string, unknown>.
type ToastCause = {
  errorId?: string;
  errorCode?: string;
  // Raw text of an error with no code, so a generic toast can still be traced to its trigger.
  errorName?: string;
  errorMessage?: string;
};

export interface SuperuserToastInfo {
  realUserSees: "message" | "nothing";
  detail?: string;
  logsUrl?: string;
  copyContext?: string;
  visibility: {
    getSnapshot: () => boolean;
    subscribe: (onChange: () => void) => () => void;
  };
}

interface ToastProps extends Omit<
  ToastOptions,
  "superuser" | "cause" | "expected"
> {
  id: string | number;
  variant: ToastVariant;
  superuser?: SuperuserToastInfo & { report: string };
}

const diagnosticsHidden = () => false;
const subscribeNever = () => () => {};

function ToastCopyButton({
  label,
  text,
  tooltip,
}: {
  label: string;
  text: string;
  tooltip: string;
}) {
  const [status, setStatus] = React.useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const buttonLabel = { idle: label, copied: "Copied", failed: "Retry copy" }[
    status
  ];
  return (
    <Tooltip
      triggerAsChild
      content={
        status === "failed"
          ? "Copy failed. Try again or select the text to copy."
          : tooltip
      }
    >
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          // Wrapped so a missing clipboard API (insecure context) rejects instead of throwing in the click handler.
          void Promise.resolve()
            .then(() => navigator.clipboard.writeText(text))
            .then(
              () => setStatus("copied"),
              () => setStatus("failed"),
            );
        }}
      >
        <span className="grid" aria-live="polite">
          <span
            className="invisible col-start-1 row-start-1"
            aria-hidden="true"
          >
            {label}
          </span>
          <span className="col-start-1 row-start-1">{buttonLabel}</span>
        </span>
      </Button>
    </Tooltip>
  );
}

function Toast({
  id,
  variant,
  title,
  description,
  action,
  onClick,
  superuser: diagnosticInfo,
}: ToastProps) {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      const visibility = diagnosticInfo?.visibility;
      if (!visibility) {
        return subscribeNever();
      }
      const update = () => {
        if (
          diagnosticInfo.realUserSees === "nothing" &&
          !visibility.getSnapshot()
        ) {
          sonnerToast.dismiss(id);
        }
        onChange();
      };
      const unsubscribe = visibility.subscribe(update);
      update();
      return unsubscribe;
    },
    [diagnosticInfo, id],
  );
  const diagnosticsVisible = React.useSyncExternalStore(
    subscribe,
    diagnosticInfo?.visibility.getSnapshot ?? diagnosticsHidden,
    diagnosticsHidden,
  );
  const superuser = diagnosticsVisible ? diagnosticInfo : undefined;
  const isInternalOnly = diagnosticInfo?.realUserSees === "nothing";
  if (isInternalOnly && !superuser) {
    return null;
  }

  const isTitleOnly = !description && !action;
  const handleClick = onClick
    ? () => {
        // Selecting diagnostic text must not trigger the notification action.
        if (window.getSelection()?.toString()) {
          return;
        }
        onClick();
        if (!superuser) {
          sonnerToast.dismiss(id);
        }
      }
    : undefined;
  return (
    <div
      onClick={handleClick}
      className={cn(
        "relative flex w-full min-w-0 rounded-xl border border-0.5 p-4 shadow-2xl",
        isInternalOnly
          ? "flex-col gap-4 border-amber-border bg-brand-yellow text-black max-h-[calc(100dvh-3rem)] overflow-y-auto"
          : "min-w-[min(268px,calc(100vw-32px))] max-w-[400px] gap-3 border-border bg-sheet text-default",
        !isInternalOnly && (isTitleOnly ? "items-center" : "items-start"),
        handleClick && "cursor-pointer",
      )}
    >
      <div
        className={
          isInternalOnly ? "flex shrink-0 items-start gap-3" : "contents"
        }
      >
        {isInternalOnly ? (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-black/10 bg-black/5">
            <Bug className="size-4" aria-hidden="true" />
          </div>
        ) : (
          <AlertIcon variant={variant} />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <span
              className={cn(
                "header-h5 select-text break-words",
                !isInternalOnly && (isTitleOnly ? "truncate" : "line-clamp-3"),
              )}
            >
              {title}
            </span>
            {description && (
              <span
                className={cn(
                  "select-text whitespace-pre-wrap break-words text-sm",
                  isInternalOnly ? "text-black/70" : "text-muted",
                  !isInternalOnly && "line-clamp-[20]",
                )}
              >
                {description}
              </span>
            )}
          </div>
          {action && (
            <Button
              variant="secondary"
              size="base"
              onClick={(event) => {
                event.stopPropagation();
                action.onClick();
                if (!superuser && action.dismissOnClick !== false) {
                  sonnerToast.dismiss(id);
                }
              }}
            >
              {action.label}
            </Button>
          )}
        </div>
        <Button
          icon
          aria-label="Dismiss notification"
          size="sm"
          variant={isInternalOnly ? "secondary" : "tertiary"}
          onClick={(event) => {
            event.stopPropagation();
            sonnerToast.dismiss(id);
          }}
        >
          <X />
        </Button>
      </div>
      {superuser && (
        <div
          role="region"
          aria-label="Superuser diagnostics"
          style={
            isInternalOnly
              ? undefined
              : {
                  width:
                    "min(var(--container-xl), calc(100vw - var(--spacing) * 8))",
                  // Sonner measures only the public toast; leave that height and the viewport inset available below the panel.
                  maxHeight:
                    "calc(100dvh - var(--initial-height, 0px) - var(--offset-bottom, 32px) - var(--spacing) * 6)",
                }
          }
          className={cn(
            "flex min-h-0 min-w-0 flex-col gap-3 text-black",
            isInternalOnly
              ? "border-t border-black/10 pt-3"
              : "absolute right-0 bottom-[calc(100%+var(--spacing)*2)] z-10 overflow-y-auto rounded-lg border border-amber-border bg-brand-yellow p-3 shadow-xl [[data-front=false]_&]:hidden",
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-semibold">Superuser only</span>
            <span className="text-xs">
              {superuser.realUserSees === "message"
                ? "Customer message below"
                : "Not shown to customers"}
            </span>
          </div>
          {superuser.detail && (
            <details
              open
              className="group min-h-0 open:flex open:flex-col open:gap-2"
            >
              <summary className="flex shrink-0 cursor-pointer list-none items-center gap-1 text-xs font-medium">
                <ChevronDown className="size-3 -rotate-90 group-open:rotate-0" />
                Technical details
              </summary>
              <pre
                aria-label="Technical details log"
                tabIndex={0}
                style={{
                  maxHeight: "min(calc(var(--spacing) * 64), 30dvh)",
                  scrollbarGutter: "stable",
                }}
                className="min-h-0 max-h-64 overflow-y-scroll overscroll-contain rounded-md bg-white/60 p-3 select-text whitespace-pre-wrap break-words font-mono text-xs text-black"
              >
                {superuser.detail}
              </pre>
            </details>
          )}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <ToastCopyButton
              label="Copy message"
              text={[title, description].filter(Boolean).join("\n")}
              tooltip="Copy the error title and message only."
            />
            <ToastCopyButton
              label="Copy for Agent"
              text={superuser.report}
              tooltip="Copy the error, technical details, and available debugger context: sandbox information, session IDs, and recent logs."
            />
            {superuser.logsUrl && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  window.open(
                    superuser.logsUrl,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                Open logs
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// NOTE (Gabe, 2026-09-23): a type alias, not an interface, so it fits analytics' Record<string, unknown>.
export type ErrorToastShownEvent = {
  variant: Extract<ToastVariant, "error" | "warning">;
  title: string;
  superuserOnly: boolean;
} & ToastCause;

type ErrorToastShownListener = (
  event: ErrorToastShownEvent,
  toast: { isOnScreen: () => boolean },
) => void;

// NOTE (Gabe, 2026-09-23): titles can interpolate raw error text; a prefix still groups cleanly.
const ERROR_TOAST_TEXT_MAX_LENGTH = 80;

const errorToastShownListeners = new Set<ErrorToastShownListener>();
const onScreenToastIds = new Set<string | number>();

/**
 * Calls `listener` when an error or warning toast appears: once per visible id (a reused id that
 * updates an on-screen toast stays silent, even if its variant or title changed), on every call for
 * toasts without an id, with the title and the cause's error text cut to 80 characters and the
 * rest of the cause when the caller passed one. Success, info and `expected` toasts never fire.
 * `toast.isOnScreen()` turns false once the toast is dismissed or auto-closes, for listeners that
 * defer reporting until the user can see it.
 */
// NOTE (Gabe, 2026-09-23): lattice is shared with the legacy editor, so hosts bring their own analytics.
export function subscribeToErrorToastShown(
  listener: ErrorToastShownListener,
): () => void {
  errorToastShownListeners.add(listener);
  return () => {
    errorToastShownListeners.delete(listener);
  };
}

function notifyErrorToastShown({
  variant,
  options,
  toastId,
}: {
  variant: Extract<ToastVariant, "error" | "warning">;
  options: ToastOptions;
  toastId: string | number;
}) {
  const { errorId, errorCode, errorName, errorMessage } = options.cause ?? {};
  const event: ErrorToastShownEvent = {
    variant,
    title: options.title.slice(0, ERROR_TOAST_TEXT_MAX_LENGTH),
    superuserOnly: options.superuser?.realUserSees === "nothing",
    ...(errorId ? { errorId } : {}),
    ...(errorCode ? { errorCode } : {}),
    ...(errorName
      ? { errorName: errorName.slice(0, ERROR_TOAST_TEXT_MAX_LENGTH) }
      : {}),
    ...(errorMessage
      ? { errorMessage: errorMessage.slice(0, ERROR_TOAST_TEXT_MAX_LENGTH) }
      : {}),
  };
  // NOTE (Gabe, 2026-09-23): listeners are analytics; a throwing one must not swallow the toast.
  const onScreen = { isOnScreen: () => onScreenToastIds.has(toastId) };
  for (const listener of errorToastShownListeners) {
    try {
      listener(event, onScreen);
    } catch (error) {
      console.error("[Toast] error-toast listener failed", error);
    }
  }
}

function forgetOnScreenToast({ id }: { id: string | number }) {
  onScreenToastIds.delete(id);
}

function showToast(variant: ToastVariant, options: ToastOptions) {
  const { title, description, duration, id, superuser } = options;
  const diagnostics = superuser
    ? {
        ...superuser,
        report: [
          `Time: ${new Date().toISOString()}`,
          superuser.realUserSees === "nothing"
            ? "Customer visibility: not shown to customers"
            : "Customer visibility: public message shown",
          "Message:",
          title,
          description,
          superuser.detail
            ? `Internal details:\n${superuser.detail}`
            : undefined,
          superuser.logsUrl ? `Logs: ${superuser.logsUrl}` : undefined,
          superuser.copyContext
            ? `Debugger context at error time:\n${superuser.copyContext}`
            : undefined,
        ]
          .filter(Boolean)
          .join("\n"),
      }
    : undefined;

  // NOTE (Gabe, 2026-09-23): a reused id updates the on-screen toast in place, so it is not a new event.
  const isUpdate = isNotNullish(id) && onScreenToastIds.has(id);
  const toastId = sonnerToast.custom(
    (toastId) => (
      <Toast
        key={diagnostics?.report}
        {...options}
        id={toastId}
        variant={variant}
        superuser={diagnostics}
      />
    ),
    {
      style:
        superuser?.realUserSees === "nothing"
          ? {
              width:
                "min(var(--container-xl), calc(100vw - var(--spacing) * 8))",
              right: 0,
              // Sonner's cached height does not follow local disclosure or visibility changes.
              height: "auto",
            }
          : undefined,
      dismissible: !superuser,
      duration: superuser ? Infinity : duration,
      ...(isNotNullish(id) ? { id } : {}),
      onDismiss: forgetOnScreenToast,
      onAutoClose: forgetOnScreenToast,
    },
  );
  onScreenToastIds.add(toastId);
  if (
    !isUpdate &&
    !options.expected &&
    (variant === "error" || variant === "warning")
  ) {
    notifyErrorToastShown({ variant, options, toastId });
  }
  return toastId;
}

interface ToastOptions {
  title: string;
  description?: string;
  action?: ToastAction;
  onClick?: () => void;
  duration?: number;
  // Reusing an id updates the toast in place so a burst of identical errors collapses into one.
  id?: string | number;
  // Pins the toast open (no auto-dismiss or swipe) so the report stays copyable; action clicks dismiss only while the panel is hidden.
  superuser?: SuperuserToastInfo;
  // NOTE (Gabe, 2026-09-23): the failure behind an error toast, for analytics only; never rendered.
  cause?: ToastCause;
  // A guard rail the user can retry (e.g. acting while a save is in flight), not a failure, so error-toast listeners skip it.
  expected?: true;
}

// Status (work in progress the user may want to open) has its own stack, bottom-left, apart from errors.
export const STATUS_TOASTER_ID = "status";
const STATUS_DONE_DURATION_MS = 5000;

interface StatusToastOptions {
  id: string;
  title: string;
  percent?: number | null;
  image?: string | null;
  onClick?: () => void;
}

// In progress: stays until the owner finishes or dismisses it.
function showStatus(id: string, pill: React.ReactElement) {
  return sonnerToast.custom(() => pill, {
    id,
    toasterId: STATUS_TOASTER_ID,
    duration: Infinity,
    dismissible: false,
  });
}

const status = Object.assign(
  ({ id, ...pill }: StatusToastOptions) => {
    return showStatus(id, <StatusPill {...pill} />);
  },
  {
    // A pill that renders its own live progress (it can read app state); same id updates in place.
    custom: showStatus,
    done: (
      id: string,
      { title, action }: { title: string; action?: StatusPillAction },
    ) => {
      return sonnerToast.custom(
        () => <StatusPillDone title={title} action={action} />,
        {
          id,
          toasterId: STATUS_TOASTER_ID,
          duration: STATUS_DONE_DURATION_MS,
        },
      );
    },
    dismiss: (id: string) => sonnerToast.dismiss(id),
  },
);

const toast = Object.assign(
  (options: ToastOptions) => {
    return showToast("success", options);
  },
  {
    status,
    custom: sonnerToast.custom,
    getToasts: sonnerToast.getToasts,
    success: (options: ToastOptions) => showToast("success", options),
    error: (options: ToastOptions) => showToast("error", options),
    warning: (options: ToastOptions) => showToast("warning", options),
    info: (options: ToastOptions) => showToast("info", options),
    // Dismiss a specific toast by id (e.g. a persistent notice), or all toasts when omitted.
    dismiss: (id?: string | number) => {
      // NOTE (Gabe, 2026-09-23): Sonner skips onDismiss with no mounted Toaster, so clear here too.
      if (isNotNullish(id)) {
        onScreenToastIds.delete(id);
      } else {
        onScreenToastIds.clear();
      }
      return sonnerToast.dismiss(id);
    },
  },
);

export { toast };
